import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { analyticsAPI } from '../services/api';
import Card from '../components/Card';
import Button from '../components/Button';
import LoadingSpinner from '../components/LoadingSpinner';
import MetricCard from '../components/analytics/MetricCard';
import CampaignPerformanceTable from '../components/analytics/CampaignPerformanceTable';
import ChannelComparison from '../components/analytics/ChannelComparison';
import TimelineChart from '../components/analytics/TimelineChart';
import InsightsPanel from '../components/analytics/InsightsPanel';
import {
  CampaignsIcon,
  LeadsIcon,
  SendIcon,
  MessageIcon,
  ClockIcon,
  SuccessIcon,
} from '../components/Icons';
import { useToast } from '../hooks/useToast';
import './Dashboard.css';

export default function Dashboard() {
  const toast = useToast();
  const [overview, setOverview] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [channels, setChannels] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('30'); // days
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    loadAnalytics();
    // Auto-refresh every 60 seconds
    const interval = setInterval(() => {
      loadAnalytics(false); // Silent refresh
    }, 60000);
    
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange]);

  const loadAnalytics = async (showLoading = true) => {
    if (showLoading) setLoading(true);
    
    try {
      // Calculate date range
      const endDate = new Date();
      const startDate = new Date();
      
      if (dateRange === 'all') {
        // All time - no start date
        var params = {};
      } else {
        startDate.setDate(startDate.getDate() - parseInt(dateRange));
        params = {
          start_date: startDate.toISOString(),
          end_date: endDate.toISOString()
        };
      }
      
      // Load all analytics data in parallel
      const [overviewRes, campaignsRes, channelsRes, timelineRes, insightsRes] = await Promise.all([
        analyticsAPI.getOverview(params),
        analyticsAPI.getCampaigns(params),
        analyticsAPI.getChannels(params),
        analyticsAPI.getTimeline(params),
        analyticsAPI.getInsights(params)
      ]);

      setOverview(overviewRes.data);
      setCampaigns(campaignsRes.data?.campaigns || []);
      setChannels(channelsRes.data?.channels || []);
      setTimeline(timelineRes.data?.timeline || []);
      setInsights(insightsRes.data?.insights || []);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Failed to load analytics:', error);
      if (showLoading) {
        toast.error('Failed to load analytics. Check console for details.');
      }
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="content-wrapper">
        <div className="page-header">
          <h1 className="page-title">Analytics Dashboard</h1>
        </div>
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="content-wrapper">
      <div className="page-header">
        <div>
          <h1 className="page-title">Analytics Dashboard</h1>
          <p className="page-subtitle">Performance metrics, insights, and ROI</p>
        </div>
        <div className="page-actions" style={{ display: 'flex', gap: 'var(--spacing-md)', alignItems: 'center' }}>
          {lastUpdated && (
            <span style={{ 
              fontSize: 'var(--font-size-xs)', 
              color: 'var(--text-secondary)',
              marginRight: 'var(--spacing-sm)'
            }}>
              Updated: {lastUpdated.toLocaleTimeString()}
            </span>
          )}
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            style={{
              padding: 'var(--spacing-sm) var(--spacing-md)',
              fontSize: 'var(--font-size-sm)',
              fontFamily: 'var(--font-family)',
              color: 'var(--text-primary)',
              background: 'var(--bg-primary)',
              border: '1px solid var(--border-color)',
              borderRadius: 'var(--border-radius)',
              cursor: 'pointer'
            }}
          >
            <option value="7">Last 7 Days</option>
            <option value="30">Last 30 Days</option>
            <option value="90">Last 90 Days</option>
            <option value="all">All Time</option>
          </select>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => loadAnalytics()}
            title="Refresh analytics"
          >
            ↻ Refresh
          </Button>
        </div>
      </div>

      {/* Overview Metrics */}
      {overview && (
        <div className="dashboard-grid">
          <MetricCard
            title="Messages Sent"
            value={overview.messages_sent}
            subtitle={`${overview.reply_rate}% reply rate`}
            icon={<SendIcon size={36} />}
            colorClass="primary"
          />
          <MetricCard
            title="Total Replies"
            value={overview.total_replies}
            subtitle={`${overview.contacted_leads} leads contacted`}
            icon={<MessageIcon size={36} />}
            colorClass="success"
          />
          <MetricCard
            title="Engagement Rate"
            value={`${overview.engagement_rate}%`}
            subtitle={`${overview.messages_engaged} engaged`}
            icon={<SuccessIcon size={36} />}
            colorClass="success"
          />
          <MetricCard
            title="Avg Time to Reply"
            value={overview.avg_time_to_reply_formatted || 'N/A'}
            subtitle="Response time"
            icon={<ClockIcon size={36} />}
            colorClass="warning"
          />
        </div>
      )}

      {/* Additional Stats Row */}
      {overview && (
        <div className="dashboard-grid" style={{ marginTop: 'var(--spacing-lg)' }}>
          <MetricCard
            title="Total Leads"
            value={overview.total_leads}
            subtitle={`${overview.contacted_leads} contacted`}
            icon={<LeadsIcon size={36} />}
            colorClass="primary"
          />
          <MetricCard
            title="Replied Leads"
            value={overview.replied_leads}
            subtitle={`${((overview.replied_leads / overview.contacted_leads) * 100).toFixed(1)}% of contacted`}
            icon={<MessageIcon size={36} />}
            colorClass="success"
          />
          <MetricCard
            title="Booked Leads"
            value={overview.booked_leads}
            subtitle={`${overview.conversion_rate}% conversion`}
            icon={<SuccessIcon size={36} />}
            colorClass="success"
          />
          <MetricCard
            title="Message Visibility"
            value={`${overview.seen_rate}%`}
            subtitle={`${overview.messages_seen} seen`}
            icon={<MessageIcon size={36} />}
            colorClass="primary"
          />
        </div>
      )}

      {/* Insights Panel */}
      <div style={{ marginTop: 'var(--spacing-xl)' }}>
        <InsightsPanel insights={insights} loading={false} />
      </div>

      {/* Campaign Performance */}
      <div style={{ marginTop: 'var(--spacing-xl)' }}>
        <CampaignPerformanceTable campaigns={campaigns} loading={false} />
      </div>

      {/* Two Column Layout */}
      <div style={{ 
        marginTop: 'var(--spacing-xl)',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
        gap: 'var(--spacing-lg)'
      }}>
        {/* Channel Comparison */}
        <ChannelComparison channels={channels} loading={false} />

        {/* Quick Actions */}
        <Card>
          <h2 className="card-title">Quick Actions</h2>
          <div className="quick-actions" style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: 'var(--spacing-sm)' 
          }}>
            <Link to="/campaigns" style={{ textDecoration: 'none' }}>
              <Button variant="primary" style={{ width: '100%' }}>
                <CampaignsIcon size={20} /> Manage Campaigns
              </Button>
            </Link>
            <Link to="/leads" style={{ textDecoration: 'none' }}>
              <Button variant="secondary" style={{ width: '100%' }}>
                <LeadsIcon size={20} /> View Leads
              </Button>
            </Link>
            <Link to="/templates" style={{ textDecoration: 'none' }}>
              <Button variant="secondary" style={{ width: '100%' }}>
                Edit Templates
              </Button>
            </Link>
            <Link to="/logs" style={{ textDecoration: 'none' }}>
              <Button variant="ghost" style={{ width: '100%' }}>
                View Logs
              </Button>
            </Link>
          </div>
        </Card>
      </div>

      {/* Timeline Chart */}
      <div style={{ marginTop: 'var(--spacing-xl)' }}>
        <TimelineChart timeline={timeline} loading={false} />
      </div>
    </div>
  );
}

